import is from '@sindresorhus/is';
import noop from 'lodash/noop';
import type { FunctionComponent } from 'react';
import { useState } from 'react';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { useTranslate } from '~/shared/context/use-translate';
import type { JobProfileVersion } from '~/shared/data-contract/job-profile-versions';
import { Badge } from '~/ui/core/badge/Badge';
import { Button } from '~/ui/core/button/Button';
import { DropdownMenuItem } from '~/ui/core/dropdown-menu/DropdownMenu';
import { IconAsterisk, IconPlus } from '~/ui/core/icons';
import { SplitDropdownButton } from '~/ui/core/split-dropdown-button/SplitDropdownButton';
import { Tooltip } from '~/ui/core/tooltip/Tooltip';
import { VersionSelect } from '~/ui/domain/version-select/VersionSelect';

import type { JobItemDetails } from '../api/use-job-item-details';
import { ActionSeparator, Action as ActionWrapper, CTAWrapper } from '../components/Action';
import { Spinner } from '../components/Spinner';
import { useFormattedVersions } from '../hooks/use-formatted-versions';
import { useRequireJobProfileTemplate } from '../hooks/use-require-job-profile-template';
import type { RegenerateDialogProps } from './RegenerateDialog';
import { RegenerateDialog, type RegenerationState } from './RegenerateDialog';

const VERSION_WRAPPER_CLASS = 'flex w-full min-w-0 flex-auto flex-row items-start gap-s';
const VERSION_SELECT_WRAPPER_CLASS = 'flex w-full min-w-0 flex-auto flex-col items-start gap-xxs';

type SaveOptions = { readonly withAutoTranslation: boolean };

const SaveAction: FunctionComponent<{
    readonly onSave: (options: SaveOptions) => Promise<void>;
}> = ({ onSave }) => {
    const translate = useTranslate();
    const [isSaving, setIsSaving] = useState(false);

    const runSave = (options: SaveOptions): void => {
        if (isSaving) return;
        setIsSaving(true);
        onSave(options)
            .catch(noop)
            .finally(() => {
                setIsSaving(false);
            });
    };

    const saveLabel = translate('common.save');
    const label = (
        <span className="relative inline-flex items-center justify-center">
            <span className={isSaving ? 'invisible' : undefined}>{saveLabel}</span>
            {isSaving ? (
                <span className="absolute inset-0 flex items-center justify-center">
                    <Spinner />
                </span>
            ) : null}
        </span>
    );
    const withoutAutoTranslationLabel = translate(
        'pages.job-architecture-v2.job-profile-save-without-auto-translation',
    );

    return (
        <SplitDropdownButton
            menuLabel={withoutAutoTranslationLabel}
            primary={{
                children: label,
                color: 'default',
                onClick: () => runSave({ withAutoTranslation: true }),
                variant: 'solid',
            }}
        >
            <DropdownMenuItem onSelect={() => runSave({ withAutoTranslation: false })}>
                {withoutAutoTranslationLabel}
            </DropdownMenuItem>
        </SplitDropdownButton>
    );
};

export const Actions: FunctionComponent<{
    readonly hasUnsavedChanges: boolean;
    readonly isArchived?: boolean;
    readonly isComparingOpen?: boolean;
    readonly isEditing: boolean;
    readonly isRegenerateDialogOpen: boolean;
    readonly isIntegrated?: boolean;
    readonly item: JobItemDetails;
    readonly onCancel: () => void;
    readonly onNewVersion: () => void;
    readonly onRegenerateDialogOpenChange: (open: boolean) => void;
    readonly onRegenerationStateChange?: (regenerationState: RegenerationState) => void;
    readonly onSave: (options: SaveOptions) => Promise<void>;
    readonly onVersionChange: (value: string) => void;
    readonly permissions: {
        readonly canEdit: boolean;
    };
    readonly regenerateDialogProps: Pick<
        RegenerateDialogProps,
        'activeLanguage' | 'formMethods' | 'onSectionTagsRegenerated' | 'profile'
    >;
    readonly selectedVersion: string | undefined;
    readonly versions: readonly JobProfileVersion[];
}> = ({
    hasUnsavedChanges,
    isArchived,
    isEditing,
    isIntegrated = false,
    isRegenerateDialogOpen,
    item,
    onCancel,
    onNewVersion,
    onRegenerateDialogOpenChange,
    onRegenerationStateChange,
    onSave,
    onVersionChange,
    permissions: { canEdit },
    regenerateDialogProps,
    selectedVersion,
    versions,
}) => {
    const translate = useTranslate();
    const { applicationState } = useApplicationContext();
    const isReviewStatusTableEnabled =
        item.type === 'job-role' || applicationState.featureFlagService.isFeatureFlagEnabled('review-status-table');
    const { formattedVersions, newTmpVersion } = useFormattedVersions(versions);
    const currentVersionNumber = is.nonEmptyStringAndNotWhitespace(selectedVersion)
        ? Number(selectedVersion)
        : Number.NaN;
    const newerVersionTarget = is.nonEmptyArray(versions)
        ? versions.find((v) => v.version === Math.max(...versions.map((vv) => vv.version)))
        : undefined;
    const hasNewerVersion =
        !is.nullOrUndefined(newerVersionTarget) &&
        Number.isFinite(currentVersionNumber) &&
        currentVersionNumber < newerVersionTarget.version;
    const { dialog: jobProfileTemplateDialog, requireJobProfileTemplate } = useRequireJobProfileTemplate(
        item.architectureId,
    );

    const editingActions = (
        <CTAWrapper>
            {hasUnsavedChanges ? (
                <Tooltip>
                    <Tooltip.Trigger asChild={true}>
                        <span style={{ display: 'inline-block' }}>
                            <Button
                                disabled={true}
                                iconPosition="leading"
                                style={{ pointerEvents: 'none' }}
                                variant="tertiary"
                            >
                                <IconAsterisk style={{ height: '16px', width: '16px' }} />
                                {translate('pages.job-architecture-v2.job-profile-regenerate')}
                            </Button>
                        </span>
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                        <Tooltip.Content side="top">
                            {translate('pages.job-architecture-v2.regenerate-disabled-dirty-form')}
                        </Tooltip.Content>
                    </Tooltip.Portal>
                </Tooltip>
            ) : (
                <RegenerateDialog
                    activeLanguage={regenerateDialogProps.activeLanguage}
                    architectureId={item.architectureId}
                    formMethods={regenerateDialogProps.formMethods}
                    isOpen={isRegenerateDialogOpen}
                    itemId={item.id}
                    itemType={item.type}
                    parentRoleId={item.type === 'job-level' && !is.null(item.parentId) ? item.parentId : undefined}
                    profile={regenerateDialogProps.profile}
                    onOpenChange={onRegenerateDialogOpenChange}
                    onRegenerationStateChange={onRegenerationStateChange}
                    onSectionTagsRegenerated={regenerateDialogProps.onSectionTagsRegenerated}
                >
                    {(isSubmitting) => (
                        <Button
                            iconPosition="leading"
                            variant="tertiary"
                            onClick={() => {
                                requireJobProfileTemplate(() => {
                                    onRegenerateDialogOpenChange(true);
                                });
                            }}
                        >
                            {isSubmitting ? <Spinner /> : <IconAsterisk style={{ height: '16px', width: '16px' }} />}
                            {translate('pages.job-architecture-v2.job-profile-regenerate')}
                        </Button>
                    )}
                </RegenerateDialog>
            )}
            <ActionSeparator />
            <Button variant="tertiary" onClick={onCancel}>
                {translate('common.cancel')}
            </Button>
            <SaveAction onSave={onSave} />
            {jobProfileTemplateDialog}
        </CTAWrapper>
    );

    const newerVersionBadge =
        isReviewStatusTableEnabled && !isEditing && hasNewerVersion ? (
            <Badge
                aria-label={translate('pages.job-architecture-v2.action-bar.newer-version-available-aria', {
                    version: newerVersionTarget.version,
                })}
                data-testid="newer-version-available-pill"
                variant="warning"
                onClick={() => {
                    onVersionChange(newerVersionTarget.version.toString());
                }}
            >
                {translate('pages.job-architecture-v2.action-bar.newer-version-available')}
            </Badge>
        ) : null;

    const newVersionButton = (
        <Button
            className={isIntegrated ? '!px-xl' : undefined}
            disabled={!canEdit || isArchived}
            iconPosition="leading"
            variant="tertiary"
            onClick={onNewVersion}
        >
            <IconPlus style={{ height: '16px', width: '16px' }} />
            <span>{translate('pages.job-architecture-v2.job-profile-new-version')}</span>
        </Button>
    );

    const defaultActions = (
        <CTAWrapper>
            {isIntegrated ? null : newerVersionBadge}
            {newVersionButton}
        </CTAWrapper>
    );

    if (isIntegrated) {
        return (
            <ActionWrapper $surface="integrated" data-testid="job-profile-actions">
                <div className={VERSION_WRAPPER_CLASS}>
                    <div className={VERSION_SELECT_WRAPPER_CLASS}>
                        <VersionSelect
                            contentInset={isEditing ? 'none' : 'table'}
                            currentVersion={
                                isEditing ? newTmpVersion : formattedVersions.find((v) => v.id === selectedVersion)
                            }
                            disabled={formattedVersions.length === 0 || isEditing}
                            placeholder={translate('pages.job-architecture-v2.job-profile-version-placeholder')}
                            versions={formattedVersions}
                            onSwitchVersion={(version) => {
                                onVersionChange(version);
                            }}
                        />
                    </div>
                </div>
                <div className="flex min-w-0 items-center">{isEditing ? null : newerVersionBadge}</div>
                {isEditing && canEdit ? editingActions : defaultActions}
            </ActionWrapper>
        );
    }

    return (
        <ActionWrapper $surface="standalone" data-testid="job-profile-actions">
            <div className={VERSION_WRAPPER_CLASS}>
                <div className={VERSION_SELECT_WRAPPER_CLASS}>
                    <VersionSelect
                        contentInset="default"
                        currentVersion={
                            isEditing ? newTmpVersion : formattedVersions.find((v) => v.id === selectedVersion)
                        }
                        disabled={formattedVersions.length === 0 || isEditing}
                        placeholder={translate('pages.job-architecture-v2.job-profile-version-placeholder')}
                        versions={formattedVersions}
                        onSwitchVersion={(version) => {
                            onVersionChange(version);
                        }}
                    />
                </div>
            </div>
            {isEditing && canEdit ? editingActions : defaultActions}
        </ActionWrapper>
    );
};
